import React, { useEffect, useState, useRef } from 'react';
import './SplashScreen.css';

const SplashScreen = ({ onComplete }) => {
  const [phase, setPhase] = useState(0); // 0: initial, 1: logo glow, 2: waves + particles, 3: tagline, 4: fade out
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);

  // Particle/firefly animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles = [];
    const particleCount = 25;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() - 0.5) * 0.5,
        speedY: (Math.random() - 0.5) * 0.5 - 0.3,
        opacity: 0,
        maxOpacity: Math.random() * 0.6 + 0.2,
        fadeSpeed: Math.random() * 0.008 + 0.003,
        fadingIn: true,
        color: Math.random() > 0.5 
          ? `rgba(167, 139, 250,` // purple
          : `rgba(251, 191, 36,`,  // gold
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(p => {
        // Fade in/out cycle
        if (p.fadingIn) {
          p.opacity += p.fadeSpeed;
          if (p.opacity >= p.maxOpacity) p.fadingIn = false;
        } else {
          p.opacity -= p.fadeSpeed;
          if (p.opacity <= 0) {
            p.fadingIn = true;
            p.opacity = 0;
            p.x = Math.random() * canvas.width;
            p.y = Math.random() * canvas.height;
          }
        }

        p.x += p.speedX;
        p.y += p.speedY;

        // Gentle floating
        p.x += Math.sin(Date.now() * 0.001 + p.y * 0.01) * 0.3;

        // Draw glow
        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 4);
        gradient.addColorStop(0, p.color + ` ${p.opacity})`);
        gradient.addColorStop(1, p.color + ` 0)`);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 4, 0, Math.PI * 2);
        ctx.fill();

        // Draw core
        ctx.fillStyle = p.color + ` ${p.opacity})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // Phase timing
  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 200),    // Logo glow starts
      setTimeout(() => setPhase(2), 800),    // Waves + particles visible
      setTimeout(() => setPhase(3), 1500),   // Tagline fades in
      setTimeout(() => setPhase(4), 3500),   // Start fade out
      setTimeout(() => onComplete?.(), 4200), // Done
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div className={`splash-screen ${phase >= 4 ? 'splash-fade-out' : ''}`}>
      {/* Background */}
      <div className="splash-bg" />
      
      {/* Particle canvas */}
      <canvas 
        ref={canvasRef} 
        className={`splash-particles ${phase >= 2 ? 'visible' : ''}`}
      />

      {/* Sound wave ripples */}
      <div className={`splash-waves ${phase >= 2 ? 'active' : ''}`}>
        <div className="wave wave-1" />
        <div className="wave wave-2" />
        <div className="wave wave-3" />
        <div className="wave wave-4" />
      </div>

      {/* Logo with glow */}
      <div className={`splash-logo-container ${phase >= 1 ? 'glowing' : ''}`}>
        <div className="splash-logo-glow" />
        <img 
          src="/logo.svg" 
          alt="EchoMemoir" 
          className="splash-logo"
        />
      </div>

      {/* App name */}
      <h1 className={`splash-title ${phase >= 1 ? 'visible' : ''}`}>
        EchoMemoir
      </h1>

      {/* Tagline */}
      <p className={`splash-tagline ${phase >= 3 ? 'visible' : ''}`}>
        Preserve voices for generations
      </p>
    </div>
  );
};

export default SplashScreen;